import { CheckCircle2, Cpu, Loader, Server, XCircle } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useJob, type Job } from '@/api/endpoints/jobs'
import { Chip } from '@/components/ui/Chip'
import { CancelJobButton } from '@/features/jobs/components/CancelJobButton'
import { llmHostLabel } from '@/features/jobs/llm'
import { queueLabel } from '@/features/jobs/queue'
import { cn } from '@/lib/cn'
import { formatDurationHuman } from '@/lib/format'
import { preservingChannel } from '@/lib/routing'
import styles from './JobProgressPanel.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface JobProgressPanelProps {
  jobId: string
  /** Called once the job reaches a terminal state (succeeded/failed/cancelled). */
  onFinished?: (job: Job) => void
}

/**
 * Live status panel for an async generation/upload/tool job. Polls via the
 * `useJob` hook (adaptive: 1.5s while pending/running, stops on terminal).
 * Renders the phase chip, optional progress bar, a status message and, on
 * success, the list of produced videos with links into the Project Detail
 * page.
 */
export function JobProgressPanel({ jobId, onFinished }: JobProgressPanelProps) {
  const { data: job, isPending, isError, error, refetch } = useJob(jobId)

  if (isPending) return <div className={styles.shell}>Aguardando...</div>
  if (isError) {
    return (
      <QueryErrorNotice
        compact
        error={error}
        label="o status do job"
        onRetry={() => {
          void refetch()
        }}
      />
    )
  }

  const variant = statusVariant(job.status)
  const isTerminal = ['succeeded', 'failed', 'cancelled'].includes(job.status)
  if (isTerminal && onFinished) onFinished(job)

  const progress = typeof job.progress === 'number' ? job.progress : 0
  const videos = extractVideos(job.detail)
  const errorBody = (job.error as { message?: string } | null)?.message
  const currentHost = job.llm_hosts?.current ?? null
  const duration = typeof job.duration_seconds === 'number' ? job.duration_seconds : null

  return (
    <div className={cn(styles.shell, styles[variant])}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          {statusIcon(job.status)}
          <Chip variant={chipVariant(job.status)}>{queueLabel(job)}</Chip>
          {job.phase ? <span className={styles.phase}>{job.phase}</span> : null}
          {/* "Onde a LLM está rodando agora" — host of the most recent local
              call, refreshed on the 1.5s poll (card #76). */}
          {!isTerminal && currentHost ? (
            <span className={styles.host} title="Onde a LLM está rodando agora">
              <HostIcon host={currentHost} /> {llmHostLabel(currentHost)}
            </span>
          ) : null}
          {isTerminal && duration != null ? (
            <span className={styles.duration}>{formatDurationHuman(duration)}</span>
          ) : null}
        </div>
        <div className={styles.headerRight}>
          <CancelJobButton job={job} />
          <span className={styles.jobId} title={job.id}>#{job.id.slice(-8)}</span>
        </div>
      </header>

      {!isTerminal ? (
        <div className={styles.progressWrap}>
          <div
            className={styles.progressFill}
            style={{ width: `${progress.toString()}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      ) : null}

      {job.message ? <p className={styles.message}>{job.message}</p> : null}

      {videos.length > 0 ? (
        <ul className={styles.videos}>
          {videos.map((v) => (
            <li key={`${v.id}-${v.language}`} className={styles.videoItem}>
              <span className={styles.videoTitle}>{v.title || v.id}</span>
              {/* Per-video generation time; "—" when the factory didn't
                  record one (card #76). */}
              <span className={styles.videoDuration}>
                {v.durationSeconds != null ? formatDurationHuman(v.durationSeconds) : '—'}
              </span>
              <Link
                to="/projects/$projectId"
                params={{ projectId: v.id }}
                search={preservingChannel({ lang: v.language })}
                className={styles.videoLink}
              >
                Abrir
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      {errorBody ? <p className={styles.errorMessage}>{errorBody}</p> : null}
    </div>
  )
}

type StatusVariant = 'running' | 'success' | 'failed' | 'pending'

function statusVariant(status: Job['status']): StatusVariant {
  switch (status) {
    case 'running':
      return 'running'
    case 'succeeded':
      return 'success'
    case 'failed':
    case 'cancelled':
      return 'failed'
    case 'pending':
    default:
      return 'pending'
  }
}

function statusIcon(status: Job['status']) {
  if (status === 'running' || status === 'pending') return <Loader aria-hidden className={styles.spin} />
  if (status === 'succeeded') return <CheckCircle2 aria-hidden />
  return <XCircle aria-hidden />
}

function chipVariant(status: Job['status']) {
  if (status === 'succeeded') return 'success'
  if (status === 'failed' || status === 'cancelled') return 'danger'
  if (status === 'running') return 'info'
  return 'default' as const
}

function HostIcon({ host }: { host: 'desktop' | 'server' }) {
  return host === 'desktop' ? (
    <Cpu aria-hidden className={styles.hostIcon} />
  ) : (
    <Server aria-hidden className={styles.hostIcon} />
  )
}

interface CompletedVideo {
  id: string
  language: string
  title: string
  durationSeconds: number | null
}

function extractVideos(detail: Job['detail']): CompletedVideo[] {
  // ``detail.videos`` is populated by the factory pipelines as each video
  // completes (see src/web/api/v1/factories/routes.py::start_viral_shorts).
  if (!detail || typeof detail !== 'object') return []
  const raw = detail['videos']
  if (!Array.isArray(raw)) return []
  return raw
    .map((v): CompletedVideo | null => {
      if (!v || typeof v !== 'object') return null
      const obj = v as Record<string, unknown>
      const id = typeof obj['id'] === 'string' ? obj['id'] : null
      const language = typeof obj['language'] === 'string' ? obj['language'] : null
      if (!id || !language) return null
      const title = typeof obj['title'] === 'string' ? obj['title'] : ''
      const durationSeconds = typeof obj['duration_seconds'] === 'number' ? obj['duration_seconds'] : null
      return { id, language, title, durationSeconds }
    })
    .filter((v): v is CompletedVideo => v !== null)
}
